import { z } from 'zod';

export const FAMILIES = ['white', 'blue', 'yellow', 'red', 'green'] as const;
export type Family = (typeof FAMILIES)[number];

const unit = z.number().min(0).max(1);
const db = z.number().min(-60).max(12);

export const SampleSourceSchema = z.object({
  kind: z.literal('sample'),
  sampleId: z.string().min(1),
  rootMidi: z.number().min(12).max(108).default(49),
  rootHz: z.number().positive().optional(),
  loop: z.object({
    start: z.number().min(0).default(0),
    end: z.number().min(0).default(0),
    mode: z.enum(['baked', 'stagger']).default('baked'),
  }).default({ start: 0, end: 0, mode: 'baked' }),
  pitchMode: z.enum(['multisample', 'resample', 'stretch']).default('multisample'),
  formantLock: z.boolean().default(false),
  semitoneOffset: z.number().min(-36).max(36).default(0),
  levelDb: db.default(0),
});

export const FreezeSourceSchema = z.object({
  kind: z.literal('freeze'),
  sampleId: z.string().min(1),
  rootMidi: z.number().min(12).max(108).default(49),
  position: unit.default(0.5),
  grain: z.object({
    sizeMs: z.number().min(10).max(1000).default(150),
    density: z.number().min(1).max(200).default(24),
    spraySec: z.number().min(0).max(5).default(0.3),
    pitchJitterCents: z.number().min(0).max(200).default(0),
    texture: unit.default(0.5),
  }).default({ sizeMs: 150, density: 24, spraySec: 0.3, pitchJitterCents: 0, texture: 0.5 }),
  stretch: z.number().min(1).max(1000).default(50),
  semitoneOffset: z.number().min(-36).max(36).default(0),
  levelDb: db.default(0),
});

export const SynthSourceSchema = z.object({
  kind: z.literal('synth'),
  engine: z.enum(['additive', 'wavetable', 'fm', 'shepard', 'noise']),
  partials: z.array(z.number().min(0).max(1)).max(32).optional(),
  wavetable: z.enum(['saw', 'square', 'triangle', 'sine', 'supersaw', 'organ']).optional(),
  fm: z.object({ ratio: z.number().min(0.25).max(16), index: z.number().min(0).max(20) }).optional(),
  shepard: z.object({ rate: z.number().min(-2).max(2), octaves: z.number().int().min(3).max(8) }).optional(),
  semitoneOffset: z.number().min(-36).max(36).default(0),
  levelDb: db.default(0),
});

export const SourceLayerSchema = z.discriminatedUnion('kind', [SampleSourceSchema, FreezeSourceSchema, SynthSourceSchema]);
export type SourceLayer = z.infer<typeof SourceLayerSchema>;

const lfoShape = z.enum(['sine', 'tri', 'random', 'perlin']);

export const DroneSchema = z.object({
  schemaVersion: z.literal(1).default(1),
  id: z.string().min(1),
  name: z.string().min(1).max(60),
  description: z.string().max(400).default(''),
  family: z.enum(FAMILIES).default('white'),
  sources: z.array(SourceLayerSchema).min(1).max(3),
  layers: z.object({
    sub: z.object({ on: z.boolean(), levelDb: db }).default({ on: false, levelDb: -9 }),
    fifth: z.object({ on: z.boolean(), levelDb: db }).default({ on: false, levelDb: -10 }),
    octaveUp: z.object({ on: z.boolean(), levelDb: db }).default({ on: false, levelDb: -12 }),
    unison: z.object({
      voices: z.number().int().min(1).max(7).default(1),
      detuneCents: z.number().min(0).max(50).default(0),
      spread: unit.default(0.5),
      phaseScatter: unit.default(1),
    }).default({ voices: 1, detuneCents: 0, spread: 0.5, phaseScatter: 1 }),
  }).default({}),
  filter: z.object({
    type: z.enum(['none', 'lp', 'hp', 'bp', 'formant', 'comb']).default('none'),
    cutoffHz: z.number().min(20).max(20000).default(4000),
    q: z.number().min(0.1).max(30).default(0.8),
    keyTrack: unit.default(0),
    combFeedback: unit.default(0.3),
    lfo: z.object({ rateHz: z.number().min(0).max(20).default(0.1), depth: unit.default(0), shape: lfoShape.default('sine') }).default({ rateHz: 0.1, depth: 0, shape: 'sine' }),
  }).default({}),
  breath: z.object({
    rateHz: z.number().min(0).max(20).default(0.1),
    depth: unit.default(0),
    shape: lfoShape.default('sine'),
    phasePerVoice: z.boolean().default(true),
  }).default({}),
  motion: z.object({
    driftAmount: unit.default(0),
    driftRateHz: z.number().min(0).max(5).default(0.05),
    targets: z.array(z.enum(['cutoff', 'detune', 'pan', 'breath'])).default(['cutoff']),
    idleEvolve: z.boolean().default(true),
  }).default({}),
  space: z.object({
    reverb: z.object({
      size: unit.default(0.5), decaySec: z.number().min(0.1).max(20).default(3), damping: unit.default(0.5),
      preDelayMs: z.number().min(0).max(200).default(10), mix: unit.default(0.2),
    }).default({}),
    delay: z.object({
      timeSec: z.number().min(0.01).max(2).default(0.4), feedback: unit.default(0), toneHz: z.number().min(200).max(16000).default(4000),
      mix: unit.default(0), pingPong: z.boolean().default(true),
    }).default({}),
    shimmer: z.object({ mix: unit.default(0), interval: z.union([z.literal(12), z.literal(7)]).default(12), feedback: unit.default(0.3) }).default({}),
    width: z.number().min(0).max(2).default(1),
  }).default({}),
  envelope: z.object({
    attackSec: z.number().min(0).max(10).default(0.15),
    releaseSec: z.number().min(0.05).max(30).default(3),
    swellCurve: z.enum(['lin', 'exp', 'sCurve']).default('exp'),
  }).default({}),
  harmony: z.object({
    autoOctave: z.boolean().default(false),
    autoFifth: z.boolean().default(false),
    ji: z.boolean().default(true),
  }).default({}),
  gesture: z.object({
    velocityTo: z.enum(['vibrato', 'buzz', 'density', 'growl', 'none']).default('vibrato'),
    radiusTo: z.enum(['brightness', 'jivari', 'grainSize', 'tilt']).default('brightness'),
    angleBendCents: z.number().min(0).max(100).default(45),
  }).default({}),
  macros: z.object({
    brightness: unit.default(0.5), motion: unit.default(0.3), density: unit.default(0.5), space: unit.default(0.4), shimmer: unit.default(0),
  }).default({}),
});

export type Drone = z.infer<typeof DroneSchema>;
export type DroneInput = z.input<typeof DroneSchema>;

/** Parse with defaults applied. Throws ZodError on invalid input. */
export const parseDrone = (input: unknown): Drone => DroneSchema.parse(input);

/** One real recording of a multisampled source at a given pitch (same loop-baked FLAC format as the primary). */
export const SampleNoteSchema = z.object({
  rootMidi: z.number(),
  rootHz: z.number().positive(),
  url: z.string(),
  loopStart: z.number(),
  loopEnd: z.number(),
  expectedFrames: z.number(),
  durationMs: z.number(),
});
export type SampleNote = z.infer<typeof SampleNoteSchema>;

export const SampleMetaSchema = z.object({
  id: z.string(),
  name: z.string(),
  family: z.string().default(''),
  url: z.string(),                 // where the FLAC lives (relative or absolute)
  durationMs: z.number(),
  sampleRate: z.number(),
  channels: z.number(),
  loopStart: z.number(),           // frames at sampleRate
  loopEnd: z.number(),
  rootHz: z.number().positive(),
  rootMidi: z.number(),
  lufs: z.number().optional(),
  expectedFrames: z.number().optional(),
  builtIn: z.boolean().default(true),
  ownerId: z.string().nullable().default(null),
  license: z.string().default(''),
  credit: z.string().default(''),
  sourceUrl: z.string().default(''),
  nc: z.boolean().default(false),
  /** other real recordings of the same source at other pitches; the engine plays the nearest one and renders only gaps */
  notes: z.array(SampleNoteSchema).optional(),
  /** noise-like material (wind, ocean, cymbal): nominal root, resampling is the right re-pitching, no vocoder rendering */
  unpitched: z.boolean().default(false),
});
export type SampleMeta = z.infer<typeof SampleMetaSchema>;
